<script setup lang="ts">
  import { useDisplay } from "vuetify";

  interface Props {
    aspectRatio?: number; // アスペクト比
    displayChatOnSide?: boolean; // チャットをサイドに表示するかどうか
    showChatCloseButton?: boolean; // チャットを閉じるボタンを表示するかどうか
  }
  const props = withDefaults(defineProps<Props>(), {
    aspectRatio: 16 / 9,
    displayChatOnSide: false,
    showChatCloseButton: true,
  });

  const isTablet = isTabletAndSpWithoutLandscapeDisplayRef();
  const isSpLandscape = isSpLandscapeRef();

  const isTabletOrSpLandscape = computed(() => {
    return isTablet.value || isSpLandscape.value;
  });

  // 親エリアの高さ
  const headerHeight = getHeaderHeightRef(true);
  const parentAreaHeight = computed(() => {
    return `calc(100vh - ${headerHeight.value}px)`;
  });

  // コメントエリアの高さ最低値
  const commentAreaMinHeight = 300;
  // コメントエリアの横幅
  const commentAreaWidth = ref(320);
  // 親エリア
  const parentArea = ref<HTMLDivElement | undefined>(undefined);

  // ビデオエリアの横幅
  const videoAreaWidth = ref<number>(100);
  // ビデオエリアの高さ
  const videoAreaHeight = ref<number>(100);
  // アスペクトエリアの横幅
  const aspectAreaWidth = ref<number>(100);
  // アスペクトエリアの高さ
  const aspectAreaHeight = ref<number>(100);
  // コメントエリアの高さ
  const commentAreaHeight = ref<number>(commentAreaMinHeight);
  // window変更も監視する
  const { width, height } = useDisplay();

  // チャットエリアを表示するかどうか
  const showChatArea = ref<boolean>(true);

  // コメントエリアの高さ設定
  // FIXME: 動画エリアのサイズに合わせてコメントエリアのmax-heightを指定する。監視以外で何かいい方法があれば採用したい。
  const setCommentAreaHeight = () => {
    if (parentArea.value) {
      if (parentArea.value.clientHeight > commentAreaMinHeight) {
        commentAreaHeight.value = parentArea.value.clientHeight;
      } else {
        commentAreaHeight.value = commentAreaMinHeight;
      }
    }
  };

  // 親エリアのサイズに合わせてアスペクト比固定のビデオエリアを作成する。
  // FIXME: divで親の縦横両方に合わせてアスペクト比固定することができなかったのでjavascriptで計算している。
  const setVideoAreaSize = () => {
    if (parentArea.value) {
      // paddingを取得
      const computedStyle = window.getComputedStyle(parentArea.value);
      const paddingRight = parseInt(computedStyle.getPropertyValue("padding-right"));
      const paddingLeft = parseInt(computedStyle.getPropertyValue("padding-left"));

      if (!props.displayChatOnSide) {
        // タブレット以下の解像度の場合

        // ビデオエリアの横幅を計算する
        videoAreaWidth.value = parentArea.value.clientWidth - paddingRight - paddingLeft;

        // アスペクト比を固定するエリアのサイズを計算する
        const parentWidth = videoAreaWidth.value;
        const aspectRatio = props.aspectRatio; // アスペクト比を設定

        // 親要素の幅に合わせる
        aspectAreaWidth.value = parentWidth;
        aspectAreaHeight.value = parentWidth / aspectRatio;

        // ビデオエリアも計算で出した縦幅にする
        videoAreaHeight.value = parentWidth / aspectRatio;
      } else {
        // ビデオエリアの横幅を計算する
        if (showChatArea.value) {
          // コメントエリアが表示されている場合はコメントエリアの幅を引く
          videoAreaWidth.value = parentArea.value.clientWidth - paddingRight - paddingLeft - commentAreaWidth.value;
        } else {
          videoAreaWidth.value = parentArea.value.clientWidth - paddingRight - paddingLeft;
        }
        videoAreaHeight.value = parentArea.value.clientHeight;

        // アスペクト比を固定するエリアのサイズを計算する
        const parentWidth = videoAreaWidth.value;
        const parentHeight = videoAreaHeight.value;
        const aspectRatio = props.aspectRatio; // アスペクト比を設定

        if (parentWidth / parentHeight < aspectRatio) {
          // 親要素の幅に合わせる場合
          aspectAreaWidth.value = parentWidth;
          aspectAreaHeight.value = parentWidth / aspectRatio;
        } else {
          // 親要素の高さに合わせる場合
          aspectAreaHeight.value = parentHeight;
          aspectAreaWidth.value = parentHeight * aspectRatio;
        }

        if (isSpLandscape.value) {
          // スマホ横向きの場合でコメントエリアが表示されている場合はコメントエリアの高さを設定する
          if (showChatArea.value) {
            commentAreaHeight.value = Math.max(aspectAreaHeight.value, commentAreaMinHeight);
          }
        }
      }
    }
  };

  // マウント時に高さを設定するする
  onMounted(() => {
    setTimeout(() => {
      setCommentAreaHeight();
      setVideoAreaSize();
    }, 200);
  });

  // windowサイズ変更時コメントエリアの高さ設定
  // videoArea.value?.clientHeightはwatchできない
  watch([width, height, showChatArea], () => {
    setCommentAreaHeight();
    setVideoAreaSize();
    setTimeout(() => {
      setCommentAreaHeight();
      setVideoAreaSize();
    }, 400);
  });
</script>

<template>
  <div class="w-100">
    <v-sheet class="d-flex align-center px-0 px-sm-4" color="transparent">
      <div class="flex-grow-1 d-flex flex-column align-center">
        <slot name="topinfo"></slot>
      </div>
      <v-sheet v-if="showChatArea" class="d-none d-md-block" :width="commentAreaWidth" color="transparent"> </v-sheet>
    </v-sheet>
    <div
      ref="parentArea"
      class="d-flex px-0 px-sm-4 justify-center align-center w-100"
      :class="{ 'parent-area': displayChatOnSide }"
      color="transparent"
    >
      <v-sheet class="d-flex align-center" style="min-height: 200px" :width="videoAreaWidth" :height="videoAreaHeight">
        <div class="d-flex align-center justify-center w-100 h-100" style="position: relative">
          <v-sheet class="aspect-area" :width="aspectAreaWidth" :height="aspectAreaHeight" color="transparent">
            <slot name="video"></slot>
          </v-sheet>
        </div>
      </v-sheet>
      <v-sheet
        v-if="displayChatOnSide"
        v-show="showChatArea"
        :min-height="commentAreaMinHeight"
        :max-height="commentAreaHeight"
        :width="commentAreaWidth"
        color="transparent"
        class="chat-area ml-4"
      >
        <!-- チャット閉じるボタン -->
        <div
          v-if="displayChatOnSide && showChatArea && showChatCloseButton"
          class="close-button d-flex justify-center align-center rounded"
          @click="showChatArea = !showChatArea"
        >
          <v-icon size="24px" color="white" icon="$mdiClose"></v-icon>
        </div>
        <slot name="chat" :height="commentAreaHeight"></slot>
      </v-sheet>
      <!-- チャット開くボタン -->
      <div
        v-if="displayChatOnSide && !showChatArea"
        class="open-button d-flex justify-center align-center rounded"
        @click="showChatArea = !showChatArea"
      >
        <v-icon size="24px" color="white" icon="$mdiChevronLeft"></v-icon>
      </div>
    </div>
    <v-sheet class="d-flex align-center px-0 px-sm-4" color="transparent">
      <div class="flex-grow-1 d-flex flex-column align-center">
        <slot name="bottominfo"></slot>
      </div>
      <v-sheet v-if="!isTabletOrSpLandscape" :width="commentAreaWidth" color="transparent"> </v-sheet>
    </v-sheet>
  </div>
</template>

<style lang="scss" scoped>
  .parent-area {
    height: v-bind(parentAreaHeight);
    max-height: v-bind(parentAreaHeight);
    position: relative;
  }
  .aspect-area {
    position: relative;
  }
  .chat-area {
    position: relative;
  }
  .close-button {
    position: absolute;
    top: 4px;
    right: 18px;
    z-index: 100;
    width: 36px;
    height: 36px;
    cursor: pointer;
    background-color: rgb(var(--v-theme-monochrome-05));
  }
  .open-button {
    position: absolute;
    top: 12px;
    right: 18px;
    z-index: 100;
    width: 36px;
    height: 36px;
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
